import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Search,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  X,
  ShoppingBag,
  ArrowLeft,
  Package,
  AlertTriangle,
  Pencil,
  PackagePlus,
  Scale,
  FileText,
  User,
  ArrowRightLeft,
  MessageSquare,
  Receipt
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : window.location.origin);

export default function POS() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cashSession, setCashSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState('efectivo');
  const [cashReceived, setCashReceived] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Extended payment options
  const [documentType, setDocumentType] = useState('boleta');
  const [paymentCondition, setPaymentCondition] = useState('contado');
  const [customerName, setCustomerName] = useState('Consumidor Final');
  const [saleNotes, setSaleNotes] = useState('');
  
  // New states for stock and price modification
  const [stockModal, setStockModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [addStockQuantity, setAddStockQuantity] = useState('');
  const [priceModal, setPriceModal] = useState(false);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [newPrice, setNewPrice] = useState('');
  
  // Scale barcode state
  const [scaleEnabled, setScaleEnabled] = useState(false);
  const searchInputRef = useRef(null);
  const barcodeBuffer = useRef('');
  const barcodeTimeout = useRef(null);

  useEffect(() => {
    fetchData();
    checkScaleConfig();
  }, []);

  const checkScaleConfig = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/scale`, { withCredentials: true });
      setScaleEnabled(data.enabled);
    } catch (error) {
      console.error('Error checking scale config:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes, sessionRes] = await Promise.all([
        axios.get(`${API_URL}/api/products?active_only=true`, { withCredentials: true }),
        axios.get(`${API_URL}/api/categories`, { withCredentials: true }),
        axios.get(`${API_URL}/api/cash-sessions/current`, { withCredentials: true })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setCashSession(sessionRes.data);
    } catch (error) {
      console.error('Error fetching POS data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Show ALL products (including zero stock)
  const filteredProducts = products.filter(product => {
    const matchesCategory = !selectedCategory || product.category_id === selectedCategory;
    const matchesSearch = !searchQuery || 
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Process barcode from scale or scanner
  const processBarcode = async (barcode) => {
    if (!barcode || barcode.length < 8) return false;
    
    // Only process if scale is enabled
    if (!scaleEnabled) return false;
    
    try {
      const { data } = await axios.post(
        `${API_URL}/api/products/decode-scale-barcode?barcode=${barcode}`,
        {},
        { withCredentials: true }
      );
      
      if (data.success && data.product) {
        // Add product to cart with scale price
        const product = data.product;
        addToCartWithPrice({
          id: product.id,
          name: product.name,
          sku: product.sku,
          price: product.scale_price,
          stock: product.stock,
          is_scale_item: true,
          scale_value: data.value,
          value_type: data.value_type
        });
        setSearchQuery('');
        toast.success(`${product.name} - ${data.value_type === 'weight' ? `${data.value}kg` : `$${data.value}`}`);
        return true;
      } else if (data.is_scale_barcode) {
        // It's a scale barcode but product not found
        toast.error(data.error || 'Producto no encontrado');
        setSearchQuery('');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error processing barcode:', error);
      return false;
    }
  };

  // Add to cart with specific price (for scale items)
  const addToCartWithPrice = useCallback((product) => {
    setCart(prev => {
      // For scale items, always add as new line (different weights)
      if (product.is_scale_item) {
        const uniqueId = `${product.id}_scale_${Date.now()}`;
        return [...prev, {
          product_id: product.id,
          unique_key: uniqueId,
          product_name: product.name,
          quantity: 1,
          unit_price: product.price,
          original_price: product.price,
          subtotal: product.price,
          stock: product.stock,
          is_scale_item: true,
          scale_value: product.scale_value,
          value_type: product.value_type
        }];
      }
      return prev;
    });
  }, []);

  // Handle search input - detect barcode scanner input
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Clear previous timeout
    if (barcodeTimeout.current) {
      clearTimeout(barcodeTimeout.current);
    }
    
    // If input looks like a barcode (numeric and 8+ digits), try to process it
    if (/^\d{8,}$/.test(value) && scaleEnabled) {
      barcodeTimeout.current = setTimeout(async () => {
        const processed = await processBarcode(value);
        if (!processed) {
          // Not a scale barcode, search normally
        }
      }, 300);
    }
  };

  // Handle Enter key in search - process barcode
  const handleSearchKeyDown = async (e) => {
    if (e.key === 'Enter' && searchQuery) {
      e.preventDefault();
      
      // Check if it's a barcode
      if (/^\d{8,}$/.test(searchQuery) && scaleEnabled) {
        const processed = await processBarcode(searchQuery);
        if (processed) return;
      }
      
      // If not a barcode or not processed, find product by SKU
      const product = products.find(p => 
        p.sku.toLowerCase() === searchQuery.toLowerCase() ||
        p.name.toLowerCase() === searchQuery.toLowerCase()
      );
      
      if (product) {
        handleProductClick(product);
        setSearchQuery('');
      }
    }
  };

  const handleProductClick = (product) => {
    if (product.stock <= 0) {
      // Show stock modal for out-of-stock products
      setSelectedProduct(product);
      setAddStockQuantity('');
      setStockModal(true);
    } else {
      addToCart(product);
    }
  };

  const addToCart = useCallback((product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        if (existing.quantity >= product.stock) {
          // Show option to add stock
          setSelectedProduct(product);
          setAddStockQuantity('');
          setStockModal(true);
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + 1, subtotal: (item.quantity + 1) * item.unit_price }
            : item
        );
      }
      return [...prev, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.price,
        original_price: product.price,
        subtotal: product.price,
        stock: product.stock
      }];
    });
    toast.success(`${product.name} agregado`);
  }, []);

  const handleAddStock = async () => {
    if (!addStockQuantity || parseInt(addStockQuantity) <= 0) {
      toast.error('Ingresa una cantidad válida');
      return;
    }
    
    try {
      const quantity = parseInt(addStockQuantity);
      await axios.patch(
        `${API_URL}/api/products/${selectedProduct.id}/quick-stock?add_quantity=${quantity}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Stock actualizado: +${quantity} unidades`);
      setStockModal(false);
      
      // Update local products
      setProducts(prev => prev.map(p => 
        p.id === selectedProduct.id 
          ? { ...p, stock: p.stock + quantity }
          : p
      ));
      
      // Optionally add to cart after adding stock
      const updatedProduct = { ...selectedProduct, stock: selectedProduct.stock + quantity };
      addToCart(updatedProduct);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar stock');
    }
  };

  const openPriceEdit = (item) => {
    // Get current stock from products
    const product = products.find(p => p.id === item.product_id);
    setEditingCartItem({
      ...item,
      stock: product?.stock || item.stock || 0
    });
    setNewPrice(item.unit_price.toString());
    setAddStockQuantity('');
    setPriceModal(true);
  };

  const handlePriceChange = () => {
    const price = parseFloat(newPrice);
    if (isNaN(price) || price < 0) {
      toast.error('Ingresa un precio válido');
      return;
    }
    
    setCart(prev => prev.map(item =>
      item.product_id === editingCartItem.product_id
        ? { ...item, unit_price: price, subtotal: item.quantity * price }
        : item
    ));
    
    toast.success('Precio actualizado');
    setPriceModal(false);
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.stock) {
          // Show option to add more stock
          const product = products.find(p => p.id === productId);
          if (product) {
            setSelectedProduct(product);
            setAddStockQuantity('');
            setStockModal(true);
          }
          return item;
        }
        return { ...item, quantity: newQty, subtotal: newQty * item.unit_price };
      }
      return item;
    }).filter(Boolean));
  };

  const removeFromCart = (idOrKey) => {
    setCart(prev => prev.filter(item => 
      (item.unique_key || item.product_id) !== idOrKey
    ));
  };

  const clearCart = () => {
    setCart([]);
  };

  const total = cart.reduce((sum, item) => sum + item.subtotal, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(amount);
  };

  const handlePayment = async () => {
    if (cart.length === 0) return;
    
    setProcessingPayment(true);
    try {
      const cashAmount = parseFloat(cashReceived) || 0;
      const changeAmount = selectedPayment === 'efectivo' ? Math.max(0, cashAmount - total) : 0;
      const netAmount = Math.round(total / 1.19);
      const taxAmount = Math.round(total - netAmount);

      const saleData = {
        items: cart.map(({ product_id, product_name, quantity, unit_price, subtotal }) => ({
          product_id, product_name, quantity, unit_price, subtotal
        })),
        payment_method: selectedPayment,
        total,
        cash_received: cashAmount,
        change_given: changeAmount,
        document_type: documentType,
        payment_condition: paymentCondition,
        customer_name: customerName,
        notes: saleNotes,
        net_amount: netAmount,
        tax_amount: taxAmount
      };

      await axios.post(`${API_URL}/api/sales`, saleData, { withCredentials: true });
      
      toast.success('Venta completada');
      setCart([]);
      setPaymentModal(false);
      setCashReceived('');
      setSelectedPayment('efectivo');
      
      // Refresh products to update stock
      const productsRes = await axios.get(`${API_URL}/api/products?active_only=true`, { withCredentials: true });
      setProducts(productsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar venta');
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  if (!cashSession) {
    return (
      <div className="min-h-screen bg-[#FAF8F5] flex items-center justify-center p-4" data-testid="pos-no-session">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-stone-900 mb-2">Caja no abierta</h2>
          <p className="text-stone-500 mb-6">Debes abrir caja antes de realizar ventas</p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/cash')}
              className="w-full h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              data-testid="go-to-cash-btn"
            >
              Ir a Caja
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full h-12 rounded-xl"
            >
              Volver al Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex" data-testid="pos-page">
      {/* Left Side - Products (65%) */}
      <div className="flex-1 lg:w-[65%] flex flex-col">
        {/* Header */}
        <header className="bg-white border-b border-stone-200 px-4 py-3 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            className="rounded-xl"
            data-testid="pos-back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-stone-900 font-heading">Punto de Venta</h1>
            <p className="text-sm text-stone-500">{user?.name} - Caja abierta</p>
          </div>
        </header>

        {/* Search */}
        <div className="p-4 bg-white border-b border-stone-200">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              ref={searchInputRef}
              type="text"
              placeholder={scaleEnabled ? "Buscar o escanear código de balanza..." : "Buscar producto por nombre o SKU..."}
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              data-testid="pos-search-input"
              className="pl-12 pr-12 h-12 rounded-xl bg-stone-50 border-stone-200 focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
            />
            {scaleEnabled && (
              <Scale className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" title="Balanza habilitada" />
            )}
          </div>
        </div>

        {/* Categories */}
        <div className="px-4 py-3 bg-white border-b border-stone-200 overflow-x-auto">
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedCategory(null)}
              data-testid="category-all"
              className={`px-4 py-2 rounded-xl whitespace-nowrap transition-all active:scale-95 ${
                !selectedCategory
                  ? 'bg-amber-600 text-white font-medium'
                  : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
              }`}
            >
              Todos
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                data-testid={`category-${cat.id}`}
                className={`px-4 py-2 rounded-xl whitespace-nowrap transition-all active:scale-95 ${
                  selectedCategory === cat.id
                    ? 'bg-amber-600 text-white font-medium'
                    : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-stone-500">
              <Package className="w-12 h-12 mb-4" />
              <p>No hay productos disponibles</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredProducts.map((product) => (
                <button
                  key={product.id}
                  onClick={() => handleProductClick(product)}
                  data-testid={`product-${product.id}`}
                  className={`bg-white rounded-2xl border p-4 text-left transition-all active:scale-[0.98] group relative ${
                    product.stock <= 0 
                      ? 'border-red-200 bg-red-50/30' 
                      : 'border-stone-200 hover:border-amber-500/30 hover:shadow-md'
                  }`}
                >
                  {/* Out of stock badge */}
                  {product.stock <= 0 && (
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Sin stock
                    </div>
                  )}
                  
                  <div className="aspect-square rounded-xl bg-stone-100 mb-3 flex items-center justify-center overflow-hidden">
                    {product.image_url ? (
                      <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <Package className="w-8 h-8 text-stone-300" />
                    )}
                  </div>
                  <h3 className={`font-medium truncate ${product.stock <= 0 ? 'text-stone-500' : 'text-stone-900 group-hover:text-amber-700'}`}>
                    {product.name}
                  </h3>
                  <p className="text-sm text-stone-500">{product.sku}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className={`font-bold ${product.stock <= 0 ? 'text-stone-400' : 'text-amber-600'}`}>
                      {formatCurrency(product.price)}
                    </p>
                    <span className={`text-xs ${product.stock <= 0 ? 'text-red-500 font-medium' : 'text-stone-400'}`}>
                      {product.stock} disp.
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Side - Cart (35%) */}
      <div className="hidden lg:flex lg:w-[35%] flex-col bg-white border-l border-stone-200 shadow-[-4px_0_24px_rgba(0,0,0,0.05)]" data-testid="pos-cart">
        <div className="p-4 border-b border-stone-200 flex items-center justify-between">
          <h2 className="text-lg font-bold text-stone-900">Ticket de Venta</h2>
          {cart.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearCart}
              data-testid="clear-cart-btn"
              className="text-red-500 hover:text-red-600 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-stone-400">
              <ShoppingBag className="w-12 h-12 mb-3" />
              <p>Carrito vacío</p>
              <p className="text-sm">Selecciona productos para agregar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item) => (
                <div key={item.unique_key || item.product_id} className={`rounded-xl p-3 ${item.is_scale_item ? 'bg-purple-50 border border-purple-200' : 'bg-stone-50'}`}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-stone-900 text-sm">{item.product_name}</h4>
                        {item.is_scale_item && (
                          <span className="text-xs text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded flex items-center gap-1">
                            <Scale className="w-3 h-3" />
                            Balanza
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-stone-500">{formatCurrency(item.unit_price)} c/u</p>
                        {item.unit_price !== item.original_price && !item.is_scale_item && (
                          <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                            Modificado
                          </span>
                        )}
                        {!item.is_scale_item && (
                          <button
                            onClick={() => openPriceEdit(item)}
                            data-testid={`edit-price-${item.product_id}`}
                            className="text-stone-400 hover:text-amber-600 p-0.5"
                            title="Modificar precio"
                          >
                            <Pencil className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => removeFromCart(item.unique_key || item.product_id)}
                      data-testid={`remove-${item.product_id}`}
                      className="text-stone-400 hover:text-red-500 p-1"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center justify-between">
                    {item.is_scale_item ? (
                      <p className="text-sm text-purple-600">
                        {item.value_type === 'weight' ? `${item.scale_value}kg` : 'Precio de etiqueta'}
                      </p>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.product_id, -1)}
                          data-testid={`decrease-${item.product_id}`}
                          className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-100 active:scale-95"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-semibold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, 1)}
                          data-testid={`increase-${item.product_id}`}
                          className="w-8 h-8 rounded-lg bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-100 active:scale-95"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <p className="font-bold text-stone-900">{formatCurrency(item.subtotal)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Total & Pay Button */}
        <div className="p-4 border-t border-stone-200 bg-stone-50">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg text-stone-600">Total</span>
            <span className="text-2xl font-bold text-stone-900" data-testid="cart-total">{formatCurrency(total)}</span>
          </div>
          <Button
            onClick={() => setPaymentModal(true)}
            disabled={cart.length === 0}
            data-testid="pay-button"
            className="w-full h-14 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-lg font-bold active:scale-[0.98] transition-all disabled:opacity-50"
          >
            Pagar
          </Button>
        </div>
      </div>

      {/* Mobile Cart Summary */}
      {cart.length > 0 && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-stone-200 p-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="text-sm text-stone-500">{cart.length} productos</p>
              <p className="text-xl font-bold text-stone-900">{formatCurrency(total)}</p>
            </div>
            <Button
              onClick={() => setPaymentModal(true)}
              data-testid="mobile-pay-btn"
              className="h-12 px-8 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold"
            >
              Pagar
            </Button>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2">
              <Receipt className="w-5 h-5 text-amber-600" />
              Confirmar Venta
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            {/* Tax breakdown */}
            <div className="bg-stone-50 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">Neto (sin IVA)</span>
                <span className="font-medium">{formatCurrency(Math.round(total / 1.19))}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-stone-600">IVA (19%)</span>
                <span className="font-medium">{formatCurrency(total - Math.round(total / 1.19))}</span>
              </div>
              <div className="flex justify-between text-lg pt-2 border-t border-stone-200">
                <span className="font-bold text-stone-900">Total a Pagar</span>
                <span className="font-bold text-amber-600">{formatCurrency(total)}</span>
              </div>
            </div>

            {/* Customer */}
            <div>
              <Label className="text-stone-700 flex items-center gap-2 mb-2">
                <User className="w-4 h-4" />
                Cliente
              </Label>
              <Input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Consumidor Final"
                data-testid="customer-name-input"
                className="h-10 rounded-xl"
              />
            </div>

            {/* Document Type */}
            <div>
              <Label className="text-stone-700 flex items-center gap-2 mb-2">
                <FileText className="w-4 h-4" />
                Tipo de Documento
              </Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'boleta', label: 'Boleta' },
                  { value: 'factura', label: 'Factura' },
                  { value: 'ticket', label: 'Ticket' }
                ].map((doc) => (
                  <button
                    key={doc.value}
                    onClick={() => setDocumentType(doc.value)}
                    data-testid={`doc-${doc.value}`}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all active:scale-95 ${
                      documentType === doc.value
                        ? 'bg-amber-600 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {doc.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Condition */}
            <div>
              <Label className="text-stone-700 mb-2">Condición de Pago</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { value: 'contado', label: 'Contado' },
                  { value: 'credito', label: 'Crédito' }
                ].map((cond) => (
                  <button
                    key={cond.value}
                    onClick={() => setPaymentCondition(cond.value)}
                    data-testid={`condition-${cond.value}`}
                    className={`py-2 px-3 rounded-xl text-sm font-medium transition-all active:scale-95 ${
                      paymentCondition === cond.value
                        ? 'bg-amber-600 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    {cond.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <Label className="text-stone-700 mb-2">Forma de Pago</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { value: 'efectivo', label: 'Efectivo', icon: Banknote },
                  { value: 'redcompra', label: 'Redcompra', icon: CreditCard },
                  { value: 'transferencia', label: 'Transfer.', icon: ArrowRightLeft }
                ].map((method) => (
                  <button
                    key={method.value}
                    onClick={() => setSelectedPayment(method.value)}
                    data-testid={`payment-${method.value}`}
                    className={`py-3 px-2 rounded-xl flex flex-col items-center gap-1 transition-all active:scale-95 ${
                      selectedPayment === method.value
                        ? 'bg-amber-600 text-white'
                        : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                    }`}
                  >
                    <method.icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{method.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Cash received - only for efectivo */}
            {selectedPayment === 'efectivo' && (
              <div>
                <Label className="text-stone-700 mb-2">Monto Recibido</Label>
                <Input
                  type="number"
                  value={cashReceived}
                  onChange={(e) => setCashReceived(e.target.value)}
                  placeholder="0"
                  data-testid="cash-received-input"
                  className="h-12 rounded-xl text-lg"
                />
                {parseFloat(cashReceived) >= total && (
                  <div className="mt-2 p-2 bg-emerald-50 rounded-xl">
                    <p className="text-center text-emerald-700 font-bold">
                      Vuelto: {formatCurrency(parseFloat(cashReceived) - total)}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            <div>
              <Label className="text-stone-700 flex items-center gap-2 mb-2">
                <MessageSquare className="w-4 h-4" />
                Observaciones (opcional)
              </Label>
              <Input
                type="text"
                value={saleNotes}
                onChange={(e) => setSaleNotes(e.target.value)}
                placeholder="Ingrese observaciones..."
                data-testid="sale-notes-input"
                className="h-10 rounded-xl"
              />
            </div>

            {/* Action buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setPaymentModal(false)}
                className="flex-1 h-12 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={handlePayment}
                disabled={processingPayment || (selectedPayment === 'efectivo' && parseFloat(cashReceived) < total)}
                data-testid="confirm-payment-btn"
                className="flex-1 h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold active:scale-[0.98]"
              >
                {processingPayment ? 'Procesando...' : 'Facturar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stock Modal - For adding stock when product is out */}
      <Dialog open={stockModal} onOpenChange={setStockModal}>
        <DialogContent className="sm:max-w-md" data-testid="stock-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2">
              <PackagePlus className="w-6 h-6 text-amber-600" />
              Agregar Stock
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="font-medium text-amber-800">{selectedProduct?.name}</p>
              <p className="text-sm text-amber-600">
                Stock actual: <span className="font-bold">{selectedProduct?.stock || 0}</span> unidades
              </p>
            </div>

            <div>
              <Label className="text-stone-700">Cantidad a agregar</Label>
              <Input
                type="number"
                value={addStockQuantity}
                onChange={(e) => setAddStockQuantity(e.target.value)}
                placeholder="Ej: 10"
                min="1"
                data-testid="add-stock-input"
                className="h-12 rounded-xl text-lg mt-2"
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStockModal(false)}
                className="flex-1 h-12 rounded-xl"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleAddStock}
                data-testid="confirm-add-stock-btn"
                className="flex-1 h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              >
                Agregar Stock
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Price Edit Modal */}
      <Dialog open={priceModal} onOpenChange={setPriceModal}>
        <DialogContent className="sm:max-w-md" data-testid="price-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-stone-900 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-amber-600" />
              Modificar Producto
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-stone-50 rounded-xl p-4">
              <p className="font-medium text-stone-900">{editingCartItem?.product_name}</p>
              <div className="flex justify-between text-sm text-stone-500 mt-1">
                <span>Precio original: <span className="font-medium">{formatCurrency(editingCartItem?.original_price || 0)}</span></span>
                <span>Stock: <span className={`font-medium ${editingCartItem?.stock <= 5 ? 'text-red-500' : ''}`}>{editingCartItem?.stock || 0}</span></span>
              </div>
            </div>

            {/* Price section */}
            <div>
              <Label className="text-stone-700">Nuevo precio</Label>
              <Input
                type="number"
                value={newPrice}
                onChange={(e) => setNewPrice(e.target.value)}
                placeholder="0"
                min="0"
                data-testid="new-price-input"
                className="h-12 rounded-xl text-lg mt-2"
              />
            </div>

            {/* Add stock section */}
            <div className="border-t border-stone-200 pt-4">
              <Label className="text-stone-700 flex items-center gap-2">
                <PackagePlus className="w-4 h-4 text-emerald-600" />
                Agregar stock (opcional)
              </Label>
              <div className="flex gap-2 mt-2">
                <Input
                  type="number"
                  value={addStockQuantity}
                  onChange={(e) => setAddStockQuantity(e.target.value)}
                  placeholder="Cantidad a agregar"
                  min="1"
                  data-testid="modal-add-stock-input"
                  className="h-12 rounded-xl flex-1"
                />
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!addStockQuantity || parseInt(addStockQuantity) <= 0) {
                      toast.error('Ingresa una cantidad válida');
                      return;
                    }
                    try {
                      const quantity = parseInt(addStockQuantity);
                      await axios.patch(
                        `${API_URL}/api/products/${editingCartItem.product_id}/quick-stock?add_quantity=${quantity}`,
                        {},
                        { withCredentials: true }
                      );
                      toast.success(`+${quantity} unidades agregadas`);
                      // Update local products and cart item
                      setProducts(prev => prev.map(p => 
                        p.id === editingCartItem.product_id 
                          ? { ...p, stock: p.stock + quantity }
                          : p
                      ));
                      setCart(prev => prev.map(item =>
                        item.product_id === editingCartItem.product_id
                          ? { ...item, stock: item.stock + quantity }
                          : item
                      ));
                      setEditingCartItem(prev => ({ ...prev, stock: prev.stock + quantity }));
                      setAddStockQuantity('');
                    } catch (error) {
                      toast.error('Error al agregar stock');
                    }
                  }}
                  data-testid="modal-confirm-stock-btn"
                  className="h-12 px-4 rounded-xl border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Agregar
                </Button>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setNewPrice(editingCartItem?.original_price?.toString() || '');
                }}
                className="h-12 rounded-xl"
              >
                Restaurar Precio
              </Button>
              <Button
                onClick={handlePriceChange}
                data-testid="confirm-price-btn"
                className="flex-1 h-12 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              >
                Aplicar Precio
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
